const ACTIVATION_CERT_KEY = 'activation_certificate';
const ACTIVATION_PORTFOLIO_KEY = 'activation_portfolio';
const ACTIVE_TENANT_KEY = 'activation_active_tenant_id';
const LICENCE_BINDING_KEY = 'licence_customer_binding';
const INSTALLATION_SESSION_TOKEN_KEY = 'installation_session_token';
const INSTALLATION_SESSION_EXPIRES_KEY = 'installation_session_expires_at';
const INSTALLATION_SESSION_TENANT_KEY = 'installation_session_tenant_id';
const LAST_RENEWAL_KEY = 'last_renewal_at';
const LAST_VERIFIED_KEY = 'last_verified_at';
const LICENCE_STATUS_KEY = 'licence_status';
const LAST_STATUS_CHECK_KEY = 'last_status_check_at';
const RENEWAL_STATE_KEY = 'renewal_state';

/** Map tenant_id → JWT d’activation (portefeuille Multi-entités). */
export type ActivationPortfolio = Record<string, string>;

/** Stockage chiffré via le processus principal Electron (safeStorage). */
export class SecureLicenceStore {
  private static memory = new Map<string, string>();

  static async isSecureStorageAvailable(): Promise<boolean> {
    return window.electron?.isSecureStorageAvailable?.() ?? false;
  }

  static async getDeviceFingerprint(): Promise<string | null> {
    return window.electron?.getDeviceFingerprint?.() ?? null;
  }

  static async getActivationCertificate(): Promise<string | null> {
    return SecureLicenceStore.getItem(ACTIVATION_CERT_KEY);
  }

  static async setActivationCertificate(jwt: string): Promise<void> {
    await SecureLicenceStore.setItem(ACTIVATION_CERT_KEY, jwt);
  }

  /** Portefeuille multi-JWT (rétrocompat : seed depuis le cert actif). */
  static async getActivationPortfolio(): Promise<ActivationPortfolio> {
    const raw = await SecureLicenceStore.getItem(ACTIVATION_PORTFOLIO_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const out: ActivationPortfolio = {};
          for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
            if (typeof k === 'string' && typeof v === 'string' && v.length > 20) {
              out[k] = v;
            }
          }
          if (Object.keys(out).length > 0) return out;
        }
      } catch {
        /* ignore */
      }
    }
    const single = await SecureLicenceStore.getActivationCertificate();
    if (!single) return {};
    const bindingRaw = await SecureLicenceStore.getLicenceBinding();
    if (bindingRaw) {
      try {
        const binding = JSON.parse(bindingRaw) as { tenant_id?: string };
        if (typeof binding.tenant_id === 'string' && binding.tenant_id.length > 0) {
          return { [binding.tenant_id]: single };
        }
      } catch {
        /* ignore */
      }
    }
    return {};
  }

  static async setActivationPortfolio(portfolio: ActivationPortfolio): Promise<void> {
    await SecureLicenceStore.setItem(ACTIVATION_PORTFOLIO_KEY, JSON.stringify(portfolio));
  }

  static async getActiveTenantId(): Promise<string | null> {
    return SecureLicenceStore.getItem(ACTIVE_TENANT_KEY);
  }

  static async setActiveTenantId(tenantId: string): Promise<void> {
    await SecureLicenceStore.setItem(ACTIVE_TENANT_KEY, tenantId);
  }

  /**
   * Enregistre un JWT dans le portefeuille et le pose comme certificat actif.
   * Sécurité : n’écrase jamais un autre tenant sans passer par assertActivationAllowed.
   */
  static async upsertPortfolioCertificate(tenantId: string, jwt: string): Promise<void> {
    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    portfolio[tenantId] = jwt;
    await SecureLicenceStore.setActivationPortfolio(portfolio);
    await SecureLicenceStore.setActiveTenantId(tenantId);
    await SecureLicenceStore.setActivationCertificate(jwt);
  }

  static async switchActiveCertificate(tenantId: string): Promise<string | null> {
    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    const jwt = portfolio[tenantId];
    if (!jwt) return null;
    await SecureLicenceStore.setActiveTenantId(tenantId);
    await SecureLicenceStore.setActivationCertificate(jwt);
    return jwt;
  }

  /** Restaure JWT + tenant + binding après un échec de bascule. */
  static async restoreActiveCertificate(
    tenantId: string,
    jwt: string,
    bindingRaw?: string | null,
  ): Promise<void> {
    await SecureLicenceStore.setActiveTenantId(tenantId);
    await SecureLicenceStore.setActivationCertificate(jwt);
    if (typeof bindingRaw === 'string' && bindingRaw.length > 0) {
      await SecureLicenceStore.setLicenceBinding(bindingRaw);
    }
  }

  static async removePortfolioTenant(tenantId: string): Promise<void> {
    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    delete portfolio[tenantId];
    await SecureLicenceStore.setActivationPortfolio(portfolio);
    for (const base of [
      LAST_RENEWAL_KEY,
      LAST_VERIFIED_KEY,
      LICENCE_STATUS_KEY,
      LAST_STATUS_CHECK_KEY,
      RENEWAL_STATE_KEY,
    ]) {
      await SecureLicenceStore.removeItem(SecureLicenceStore.tenantKey(base, tenantId));
    }
  }

  static async getLicenceBinding(): Promise<string | null> {
    return SecureLicenceStore.getItem(LICENCE_BINDING_KEY);
  }

  static async setLicenceBinding(raw: string): Promise<void> {
    await SecureLicenceStore.setItem(LICENCE_BINDING_KEY, raw);
  }

  static async clearLicenceBinding(): Promise<void> {
    await SecureLicenceStore.removeItem(LICENCE_BINDING_KEY);
  }

  /**
   * État de licence PAR ENTREPRISE (P0-3) : une révocation, un statut ou un renouvellement
   * ne concernent que le certificat du tenant indiqué, jamais tout le poste.
   * Repli de lecture sur l'ancienne clé globale pour l'entreprise active (migration).
   */
  private static tenantKey(base: string, tenantId: string): string {
    return `${base}::${tenantId}`;
  }

  private static async getTenantScoped(base: string, tenantId: string): Promise<string | null> {
    const scoped = await SecureLicenceStore.getItem(SecureLicenceStore.tenantKey(base, tenantId));
    if (scoped !== null) return scoped;
    const legacy = await SecureLicenceStore.getItem(base);
    if (legacy === null) return null;
    const active = await SecureLicenceStore.getActiveTenantId();
    return active === tenantId ? legacy : null;
  }

  private static async setTenantScoped(base: string, tenantId: string, v: string): Promise<void> {
    await SecureLicenceStore.setItem(SecureLicenceStore.tenantKey(base, tenantId), v);
    await SecureLicenceStore.removeLegacyIfActive(base, tenantId);
  }

  private static async clearTenantScoped(base: string, tenantId: string): Promise<void> {
    await SecureLicenceStore.removeItem(SecureLicenceStore.tenantKey(base, tenantId));
    await SecureLicenceStore.removeLegacyIfActive(base, tenantId);
  }

  private static async removeLegacyIfActive(base: string, tenantId: string): Promise<void> {
    if ((await SecureLicenceStore.getActiveTenantId()) === tenantId) {
      await SecureLicenceStore.removeItem(base);
    }
  }

  static async getLastRenewalAt(tenantId: string): Promise<string | null> {
    return SecureLicenceStore.getTenantScoped(LAST_RENEWAL_KEY, tenantId);
  }

  static async setLastRenewalAt(tenantId: string, iso: string): Promise<void> {
    await SecureLicenceStore.setTenantScoped(LAST_RENEWAL_KEY, tenantId, iso);
  }

  static async getLastVerifiedAt(tenantId: string): Promise<string | null> {
    return SecureLicenceStore.getTenantScoped(LAST_VERIFIED_KEY, tenantId);
  }

  static async setLastVerifiedAt(tenantId: string, iso: string): Promise<void> {
    await SecureLicenceStore.setTenantScoped(LAST_VERIFIED_KEY, tenantId, iso);
  }

  /** État « révoquée / suspendue » de CETTE entreprise, persisté dans le coffre (JSON). */
  static async getLicenceStatus(tenantId: string): Promise<string | null> {
    return SecureLicenceStore.getTenantScoped(LICENCE_STATUS_KEY, tenantId);
  }

  static async setLicenceStatus(tenantId: string, raw: string): Promise<void> {
    await SecureLicenceStore.setTenantScoped(LICENCE_STATUS_KEY, tenantId, raw);
  }

  static async clearLicenceStatus(tenantId: string): Promise<void> {
    await SecureLicenceStore.clearTenantScoped(LICENCE_STATUS_KEY, tenantId);
  }

  /** Dernier contrôle de statut serveur réussi (JWT vérifié) pour cette entreprise. */
  /** Dernier résultat du renouvellement automatique (JSON, voir LicenceRenewalStatus). */
  static async getRenewalState(tenantId: string): Promise<string | null> {
    return SecureLicenceStore.getItem(SecureLicenceStore.tenantKey(RENEWAL_STATE_KEY, tenantId));
  }

  static async setRenewalState(tenantId: string, raw: string): Promise<void> {
    await SecureLicenceStore.setItem(SecureLicenceStore.tenantKey(RENEWAL_STATE_KEY, tenantId), raw);
  }

  static async getLastStatusCheckAt(tenantId: string): Promise<string | null> {
    return SecureLicenceStore.getTenantScoped(LAST_STATUS_CHECK_KEY, tenantId);
  }

  static async setLastStatusCheckAt(tenantId: string, iso: string): Promise<void> {
    await SecureLicenceStore.setTenantScoped(LAST_STATUS_CHECK_KEY, tenantId, iso);
  }

  /** Enregistre un certificat renouvelé SANS changer l'entreprise active. */
  static async setPortfolioCertificate(tenantId: string, jwt: string): Promise<void> {
    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    portfolio[tenantId] = jwt;
    await SecureLicenceStore.setActivationPortfolio(portfolio);
  }

  static async clearActivation(): Promise<void> {
    const tenantIds = Object.keys(await SecureLicenceStore.getActivationPortfolio());
    for (const tenantId of tenantIds) {
      for (const base of [
        LAST_RENEWAL_KEY,
        LAST_VERIFIED_KEY,
        LICENCE_STATUS_KEY,
        LAST_STATUS_CHECK_KEY,
      ]) {
        await SecureLicenceStore.removeItem(SecureLicenceStore.tenantKey(base, tenantId));
      }
    }
    await SecureLicenceStore.removeItem(ACTIVATION_CERT_KEY);
    await SecureLicenceStore.removeItem(ACTIVATION_PORTFOLIO_KEY);
    await SecureLicenceStore.removeItem(ACTIVE_TENANT_KEY);
    await SecureLicenceStore.clearLicenceBinding();
    await SecureLicenceStore.removeItem(INSTALLATION_SESSION_TOKEN_KEY);
    await SecureLicenceStore.removeItem(INSTALLATION_SESSION_EXPIRES_KEY);
    await SecureLicenceStore.removeItem(INSTALLATION_SESSION_TENANT_KEY);
    await SecureLicenceStore.removeItem(LAST_RENEWAL_KEY);
    await SecureLicenceStore.removeItem(LAST_VERIFIED_KEY);
    await SecureLicenceStore.removeItem(LICENCE_STATUS_KEY);
    await SecureLicenceStore.removeItem(LAST_STATUS_CHECK_KEY);
  }

  static async getInstallationSessionToken(): Promise<string | null> {
    return SecureLicenceStore.getItem(INSTALLATION_SESSION_TOKEN_KEY);
  }

  static async getInstallationSessionExpiresAt(): Promise<string | null> {
    return SecureLicenceStore.getItem(INSTALLATION_SESSION_EXPIRES_KEY);
  }

  static async getInstallationSessionTenantId(): Promise<string | null> {
    return SecureLicenceStore.getItem(INSTALLATION_SESSION_TENANT_KEY);
  }

  static async setInstallationSessionToken(
    token: string,
    expiresAt: string,
    tenantId: string,
  ): Promise<void> {
    await SecureLicenceStore.setItem(INSTALLATION_SESSION_TOKEN_KEY, token);
    await SecureLicenceStore.setItem(INSTALLATION_SESSION_EXPIRES_KEY, expiresAt);
    await SecureLicenceStore.setItem(INSTALLATION_SESSION_TENANT_KEY, tenantId);
  }

  static async clearInstallationSession(): Promise<void> {
    await SecureLicenceStore.removeItem(INSTALLATION_SESSION_TOKEN_KEY);
    await SecureLicenceStore.removeItem(INSTALLATION_SESSION_EXPIRES_KEY);
    await SecureLicenceStore.removeItem(INSTALLATION_SESSION_TENANT_KEY);
  }

  private static async getItem(key: string): Promise<string | null> {
    if (window.electron?.secureStoreGet) {
      return window.electron.secureStoreGet(key);
    }
    return SecureLicenceStore.memory.get(key) ?? null;
  }

  private static async setItem(key: string, value: string): Promise<void> {
    if (window.electron?.secureStoreSet) {
      await window.electron.secureStoreSet(key, value);
      return;
    }
    SecureLicenceStore.memory.set(key, value);
  }

  private static async removeItem(key: string): Promise<void> {
    if (window.electron?.secureStoreRemove) {
      await window.electron.secureStoreRemove(key);
      return;
    }
    SecureLicenceStore.memory.delete(key);
  }

  /** Utilisé par les tests navigateur (hors Electron). */
  static __resetForTests(): void {
    SecureLicenceStore.memory.clear();
  }
}
